import { Canvas, Cell, Columns, Img, Line, PdfMakeWrapper, Stack, Table, Txt } from 'pdfmake-wrapper';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';

import type { MonthlyReport, MonthlyReportIncident } from '../monthly-report.models';
import { hasDailyTrafficPoints } from '../utils/monthly-report-chart.util';
import {
  AVAILABILITY_SOURCE_NOTE,
  INCOMPLETE_AVAILABILITY_HINT,
  TRAFFIC_NO_DATA_NOTE,
  TRAFFIC_UNAVAILABLE_NOTE,
  buildMonthlyReportPdfFilename,
  formatBytesSi,
  formatDurationSeconds,
  formatLocality,
  formatMbps,
  formatPdfContractedPlan,
  formatPdfIncidentPeriod,
  formatPercent,
  formatUgelContext,
  ticketCategoryLabel,
  ticketStatusLabel,
} from '../utils/monthly-report-format.util';
import { formatLimaMonthLabel } from '../../institution-history/utils/traffic-format.util';
import {
  MONTHLY_REPORT_PDF_EMPTY_INCIDENTS,
  MONTHLY_REPORT_PDF_FOOTER_CONTACT,
  MONTHLY_REPORT_PDF_FOOTER_UNIT,
  MONTHLY_REPORT_PDF_INCIDENTS_NOTE,
  MONTHLY_REPORT_PDF_LOGO_FIT,
  MONTHLY_REPORT_PDF_LOGO_PATH,
  MONTHLY_REPORT_PDF_PROVIDER,
  MONTHLY_REPORT_PDF_SERVICE_NAME,
  MONTHLY_REPORT_PDF_SERVICE_STATUS_LABEL,
  MONTHLY_REPORT_PDF_UNAVAILABLE_AVAILABILITY,
} from './monthly-report-pdf.constants';

export interface DownloadMonthlyReportPdfInput {
  report: MonthlyReport;
  chartImage?: string | null;
  generatedAt: Date;
}

/** Huecos verticales secundarios: un poco más compactos solo si hay incidencias. */
type MonthlyReportPdfDensity = {
  headerLineBottom: number;
  sectionTitleTop: number;
  sectionTitleBottom: number;
  blockGap: number;
  chartPadY: number;
  chartDividerTop: number;
  chartDividerBottom: number;
  kpiNoteTop: number;
  incidentsNoteTop: number;
};

function layoutDensity(hasIncidents: boolean): MonthlyReportPdfDensity {
  if (hasIncidents) {
    return {
      headerLineBottom: 6,
      sectionTitleTop: 5,
      sectionTitleBottom: 3,
      blockGap: 5,
      chartPadY: 6,
      chartDividerTop: 3,
      chartDividerBottom: 4,
      kpiNoteTop: 3,
      incidentsNoteTop: 4,
    };
  }
  return {
    headerLineBottom: 10,
    sectionTitleTop: 8,
    sectionTitleBottom: 5,
    blockGap: 8,
    chartPadY: 8,
    chartDividerTop: 4,
    chartDividerBottom: 5,
    kpiNoteTop: 4,
    incidentsNoteTop: 6,
  };
}

let fontsReady = false;

function ensurePdfFonts(): void {
  if (fontsReady) {
    return;
  }
  PdfMakeWrapper.setFonts(pdfFonts);
  fontsReady = true;
}

export async function downloadMonthlyReportPdf(input: DownloadMonthlyReportPdfInput): Promise<void> {
  ensurePdfFonts();

  const { report, generatedAt } = input;
  const density = layoutDensity(report.incidents.length > 0);
  const pdf = new PdfMakeWrapper();
  pdf.pageSize('A4');
  pdf.pageOrientation('portrait');
  pdf.pageMargins([40, 32, 40, 48]);
  pdf.info({
    title: `Reporte de conectividad ${report.yearMonth}`,
    author: MONTHLY_REPORT_PDF_PROVIDER.name,
    subject: 'Reporte mensual de conectividad IE',
  });
  pdf.defaultStyle({
    fontSize: 9,
    color: '#334155',
  });
  pdf.footer(
    (currentPage: number, pageCount: number) =>
      new Columns([
        new Stack([
          new Txt(`Generado: ${formatGeneratedAt(generatedAt)} · ${MONTHLY_REPORT_PDF_FOOTER_UNIT}`)
            .fontSize(8)
            .color('#94a3b8')
            .end,
          new Txt(MONTHLY_REPORT_PDF_FOOTER_CONTACT)
            .fontSize(7)
            .color('#94a3b8')
            .margin([0, 1.5, 0, 0])
            .end,
        ]).width('*').end,
        new Txt(`Página ${currentPage} de ${pageCount}`)
          .fontSize(8)
          .color('#94a3b8')
          .alignment('right')
          .width('auto')
          .end,
      ]).margin([40, 8, 40, 16]).end,
  );

  const logo = await new Img(MONTHLY_REPORT_PDF_LOGO_PATH).fit(MONTHLY_REPORT_PDF_LOGO_FIT).build();

  // Cabecera compacta en dos columnas: izquierda título/producto/período/estado, derecha logo + proveedor/RUC
  pdf.add(
    new Columns([
      new Stack([
        new Txt('REPORTE DEL SERVICIO').bold().fontSize(15).color('#1d4ed8').margin([0, 0, 0, 2]).end,
        new Columns([
          new Txt(MONTHLY_REPORT_PDF_SERVICE_NAME).bold().fontSize(12).color('#0f172a').width('auto').end,
          new Stack([statusBadge()]).width('auto').margin([0, 1.5, 0, 0]).end,
        ])
          .columnGap(10)
          .margin([0, 0, 0, 4]).end,
        new Txt([
          new Txt('Período: ').color('#64748b').fontSize(11).end,
          new Txt(formatLimaMonthLabel(report.yearMonth)).bold().color('#334155').fontSize(11).end,
        ]).end,
      ]).width('*').end,
      new Stack([
        new Columns(['', new Stack([logo]).width(54).end]).end,
        new Txt(MONTHLY_REPORT_PDF_PROVIDER.name)
          .bold()
          .alignment('right')
          .fontSize(7.5)
          .color('#0f172a')
          .margin([0, 2, 0, 0]).end,
        new Txt(`RUC: ${MONTHLY_REPORT_PDF_PROVIDER.ruc}`)
          .alignment('right')
          .fontSize(7)
          .color('#64748b')
          .margin([0, 0.5, 0, 0]).end,
      ]).width(140).end,
    ]).end,
  );

  // Línea divisoria azul debajo del encabezado
  pdf.add(
    new Canvas([new Line([0, 0], [515, 0]).lineColor('#1d4ed8').lineWidth(1.5).end])
      .margin([0, 2, 0, density.headerLineBottom])
      .end,
  );

  pdf.add(clientServiceCard(report, density));
  pdf.add(sectionTitle('INDICADORES DEL PERÍODO', density));
  pdf.add(kpiBlock(report, density));
  pdf.add(sectionTitle('COMPORTAMIENTO DEL TRÁFICO', density));
  pdf.add(await trafficChartCard(report, input.chartImage, density));
  pdf.add(sectionTitle('INCIDENCIAS REGISTRADAS DURANTE EL PERÍODO', density));
  pdf.add(incidentsTable(report));
  if (report.incidents.length) {
    pdf.add(
      new Txt(MONTHLY_REPORT_PDF_INCIDENTS_NOTE)
        .fontSize(7.5)
        .color('#64748b')
        .margin([0, density.incidentsNoteTop, 0, 0])
        .end,
    );
  }

  pdf.create().download(buildMonthlyReportPdfFilename(report.header?.modularCode, report.serviceId, report.yearMonth));
}

/** Espiable en Karma: el export ESM de la función no es writable. */
export const monthlyReportPdf = {
  download: downloadMonthlyReportPdf,
};

function statusBadge() {
  return new Table([
    [
      new Cell(
        new Txt([
          new Txt('ESTADO: ').fontSize(7).bold().color('#166534').end,
          new Txt(MONTHLY_REPORT_PDF_SERVICE_STATUS_LABEL).fontSize(7.5).bold().color('#166534').end,
        ]).alignment('center').end,
      ).fillColor('#ecfdf5').end,
    ],
  ])
    .widths(['auto'])
    .layout({
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => '#a7f3d0',
      vLineColor: () => '#a7f3d0',
      paddingLeft: () => 6,
      paddingRight: () => 6,
      paddingTop: () => 1.5,
      paddingBottom: () => 1.5,
    }).end;
}

function sectionTitle(text: string, density: MonthlyReportPdfDensity) {
  return new Txt(text)
    .bold()
    .fontSize(9.5)
    .color('#1d4ed8')
    .margin([0, density.sectionTitleTop, 0, density.sectionTitleBottom])
    .end;
}

function clientServiceCard(report: MonthlyReport, density: MonthlyReportPdfDensity) {
  return new Table([
    [
      new Cell(
        new Stack([
          new Txt('Datos del Cliente y Servicio')
            .bold()
            .fontSize(10)
            .color('#0f172a')
            .margin([0, 0, 0, 3])
            .end,
          new Canvas([new Line([0, 0], [479, 0]).lineColor('#e2e8f0').lineWidth(0.5).end])
            .margin([0, 0, 0, 4])
            .end,
          clientServiceFieldsTable(report),
        ]).end,
      ).fillColor('#f8fafc').end,
    ],
  ])
    .widths(['*'])
    .layout({
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => '#e2e8f0',
      vLineColor: () => '#e2e8f0',
      paddingLeft: () => 18,
      paddingRight: () => 18,
      paddingTop: () => 8,
      paddingBottom: () => 8,
    })
    .margin([0, 0, 0, density.blockGap]).end;
}

function clientServiceFieldsTable(report: MonthlyReport) {
  const header = report.header;
  return new Table([
    [
      clientFieldCell('Razón Social / UGEL', formatUgelContext(header?.ugel) || '—'),
      clientFieldCell('RUC', header?.ugelRuc?.trim() || '—'),
    ],
    [
      clientFieldCell('Establecimiento / Usuario', header?.institutionName?.trim() || '—'),
      clientFieldCell('Plan contratado', formatPdfContractedPlan(header)),
    ],
    [
      clientFieldCell('ID Servicio / Circuito', String(report.serviceId), { highlight: true }),
      clientFieldCell('Dirección IP (WAN)', header?.routerIp?.trim() || '—'),
    ],
    [
      clientFieldCell('Código modular', header?.modularCode?.trim() || '—'),
      clientFieldCell('Localidad', formatLocality(header)),
    ],
  ])
    .widths(['*', '*'])
    .layout({
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingLeft: (i) => (i === 0 ? 0 : 16),
      paddingRight: (i) => (i === 0 ? 16 : 0),
      paddingTop: () => 2,
      paddingBottom: () => 3,
    }).end;
}

function idServicioTag(value: string) {
  return new Table([
    [new Cell(new Txt(value).fontSize(9).bold().color('#1d4ed8').alignment('center').end).fillColor('#eff6ff').end],
  ])
    .widths(['auto'])
    .layout({
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => '#bfdbfe',
      vLineColor: () => '#bfdbfe',
      paddingLeft: () => 7,
      paddingRight: () => 7,
      paddingTop: () => 1.5,
      paddingBottom: () => 1.5,
    }).end;
}

function clientFieldCell(label: string, value: string, options?: { highlight?: boolean }) {
  if (options?.highlight) {
    return new Cell(
      new Stack([
        new Txt(label).fontSize(7).color('#64748b').end,
        new Columns([new Stack([idServicioTag(value)]).width('auto').end]).margin([0, 2, 0, 0]).end,
      ]).end,
    ).end;
  }

  return new Cell(
    new Stack([
      new Txt(label).fontSize(7).color('#64748b').end,
      new Txt(value).fontSize(9).bold().color('#0f172a').margin([0, 2, 0, 0]).end,
    ]).end,
  ).end;
}

function kpiBlock(report: MonthlyReport, density: MonthlyReportPdfDensity) {
  const kpis = report.traffic.monthlyKpis;
  const availability = availabilityPresentation(report);
  const notes: string[] = [];
  if (availability.hint) notes.push(availability.hint);
  if (report.traffic.dataStatus === 'PARTIAL') notes.push(TRAFFIC_UNAVAILABLE_NOTE);
  if (report.traffic.dataStatus === 'NO_DATA' || report.traffic.identityInconsistent) {
    notes.push(TRAFFIC_NO_DATA_NOTE);
  }

  const availabilityCard = buildAvailabilityCard(availability);
  const trafficCard1 = miniKpiCard('DESCARGA TOTAL', formatBytesSi(kpis?.downloadBytes) ?? '—');
  const trafficCard2 = miniKpiCard('SUBIDA TOTAL', formatBytesSi(kpis?.uploadBytes) ?? '—');
  const trafficCard3 = miniKpiCard('PICO DE BAJADA', formatMbps(kpis?.downloadMaxMbps));
  const trafficCard4 = miniKpiCard('PICO DE SUBIDA', formatMbps(kpis?.uploadMaxMbps));

  const trafficGrid = new Stack([
    new Columns([trafficCard1, trafficCard2]).columnGap(8).end,
    new Columns([trafficCard3, trafficCard4]).columnGap(8).margin([0, 5, 0, 0]).end,
  ]).end;

  const kpiColumns = new Columns([
    new Stack([availabilityCard]).width(165).end,
    new Stack([trafficGrid]).width('*').end,
  ]).columnGap(10).end;

  const items: any[] = [kpiColumns];

  notes.forEach((note) => {
    items.push(new Txt(note).fontSize(7.5).color('#64748b').margin([0, density.kpiNoteTop, 0, 0]).end);
  });
  items.push(
    new Txt(AVAILABILITY_SOURCE_NOTE).fontSize(7.5).color('#64748b').margin([0, density.kpiNoteTop, 0, 0]).end,
  );

  return new Stack(items).end;
}

function buildAvailabilityCard(availability: {
  value: string;
  isComplete: boolean;
  caption?: string;
  hint?: string;
}) {
  const isComplete = availability.isComplete;
  const valColor = isComplete ? '#15803d' : '#0f172a';
  const valSize = availability.value.length > 10 ? 9.5 : 24;
  const padY = availability.caption ? 14 : 18;

  const stackItems: any[] = [
    new Txt('DISPONIBILIDAD')
      .fontSize(7.5)
      .bold()
      .color(isComplete ? '#15803d' : '#64748b').end,
    new Txt(availability.value)
      .fontSize(valSize)
      .bold()
      .color(valColor)
      .margin([0, 6, 0, availability.caption ? 3 : 0]).end,
  ];

  if (availability.caption) {
    stackItems.push(new Txt(availability.caption).fontSize(7.5).color('#64748b').end);
  }

  return new Table([
    [
      new Cell(
        new Stack(stackItems).end,
      ).fillColor('#f8fafc').end,
    ],
  ])
    .widths(['*'])
    .layout({
      hLineWidth: () => 0.5,
      vLineWidth: (i) => (i === 0 ? 4 : 0.5),
      hLineColor: () => '#e2e8f0',
      vLineColor: (i) => (i === 0 ? (isComplete ? '#16a34a' : '#cbd5e1') : '#e2e8f0'),
      paddingLeft: () => 14,
      paddingRight: () => 14,
      paddingTop: () => padY,
      paddingBottom: () => padY,
    }).end;
}

function miniKpiCard(label: string, value: string) {
  return new Table([
    [
      new Cell(
        new Stack([
          new Txt(label).fontSize(7).bold().color('#64748b').end,
          new Txt(value).fontSize(13).bold().color('#0f172a').margin([0, 3, 0, 0]).end,
        ]).end,
      ).fillColor('#f8fafc').end,
    ],
  ])
    .widths(['*'])
    .layout({
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => '#e2e8f0',
      vLineColor: () => '#e2e8f0',
      paddingLeft: () => 14,
      paddingRight: () => 14,
      paddingTop: () => 6,
      paddingBottom: () => 6,
    }).end;
}

function availabilityPresentation(report: MonthlyReport): {
  value: string;
  isComplete: boolean;
  caption?: string;
  hint?: string;
} {
  if (report.availability.status === 'COMPLETE') {
    return {
      value: formatPercent(report.availability.percent) ?? '—',
      isComplete: true,
    };
  }
  if (report.availability.status === 'INCOMPLETE') {
    return {
      value: '—',
      isComplete: false,
      caption: 'Pendiente de revisión',
      hint: INCOMPLETE_AVAILABILITY_HINT,
    };
  }
  return {
    value: MONTHLY_REPORT_PDF_UNAVAILABLE_AVAILABILITY,
    isComplete: false,
  };
}

async function trafficChartCard(
  report: MonthlyReport,
  chartImage: string | null | undefined,
  density: MonthlyReportPdfDensity,
) {
  const hasPoints = hasDailyTrafficPoints(report.traffic.dailySeries);
  let chartContent: any;

  if (!hasPoints) {
    chartContent = new Table([
      [
        new Cell(
          new Stack([
            new Txt(TRAFFIC_NO_DATA_NOTE)
              .italics()
              .fontSize(8.5)
              .color('#64748b')
              .alignment('center')
              .margin([0, 16, 0, 16])
              .end,
          ]).end,
        )
          .fillColor('#ffffff')
          .end,
      ],
    ])
      .widths(['*'])
      .layout({
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => '#e2e8f0',
        vLineColor: () => '#e2e8f0',
      })
      .end;
  } else if (chartImage) {
    const image = await new Img(chartImage).fit([479, 180]).alignment('center').build();
    chartContent = new Table([
      [
        new Cell(new Stack([image]).alignment('center').end)
          .fillColor('#ffffff')
          .end,
      ],
    ])
      .widths(['*'])
      .layout({
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => '#e2e8f0',
        vLineColor: () => '#e2e8f0',
        paddingLeft: () => 2,
        paddingRight: () => 2,
        paddingTop: () => 2,
        paddingBottom: () => 2,
      })
      .end;
  } else {
    chartContent = new Stack([]).end;
  }

  const cardItems: any[] = [
    new Txt(`Evolución diaria del tráfico · ${formatLimaMonthLabel(report.yearMonth)}`)
      .fontSize(7.5)
      .color('#64748b')
      .end,
    new Canvas([new Line([0, 0], [487, 0]).lineColor('#e2e8f0').lineWidth(0.5).end])
      .margin([0, density.chartDividerTop, 0, density.chartDividerBottom])
      .end,
    chartContent,
  ];

  if (report.traffic.dataStatus === 'PARTIAL') {
    cardItems.push(
      new Txt(TRAFFIC_UNAVAILABLE_NOTE)
        .fontSize(7.5)
        .color('#64748b')
        .margin([0, 6, 0, 0])
        .end,
    );
  }

  return new Table([
    [
      new Cell(
        new Stack(cardItems).end,
      ).fillColor('#f8fafc').end,
    ],
  ])
    .widths(['*'])
    .layout({
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => '#e2e8f0',
      vLineColor: () => '#e2e8f0',
      paddingLeft: () => 14,
      paddingRight: () => 14,
      paddingTop: () => density.chartPadY,
      paddingBottom: () => density.chartPadY,
    })
    .margin([0, 0, 0, density.blockGap])
    .end;
}

function incidentsTable(report: MonthlyReport) {
  const header = [
    headerCell('Código'),
    headerCell('Período'),
    headerCell('Tipo'),
    headerCell('Descripción'),
    headerCell('Duración'),
    headerCell('Estado'),
  ];
  const widths = [62, 110, 70, '*', 50, 60];

  if (!report.incidents.length) {
    return new Table([
      header,
      [
        new Cell(new Txt(MONTHLY_REPORT_PDF_EMPTY_INCIDENTS).italics().color('#64748b').end)
          .colSpan(6)
          .alignment('center')
          .margin([4, 8, 4, 8]).end,
        {},
        {},
        {},
        {},
        {},
      ],
    ])
      .headerRows(1)
      .widths(widths)
      .layout('lightHorizontalLines').end;
  }

  const body = report.incidents.map((incident) => incidentRow(incident, report.monthStart, report.monthEnd));
  return new Table([header, ...body]).headerRows(1).widths(widths).layout('lightHorizontalLines').end;
}

function incidentRow(incident: MonthlyReportIncident, monthStart: string, monthEnd: string) {
  return [
    bodyCell(incident.code?.trim() || '—', { noWrap: true }),
    bodyCell(formatPdfIncidentPeriod(incident, monthStart, monthEnd), { noWrap: true }),
    bodyCell(ticketCategoryLabel(incident.category)),
    bodyCell(incident.subject?.trim() || '—'),
    bodyCell(formatDurationSeconds(incident.clippedDurationSeconds) ?? '—'),
    bodyCell(ticketStatusLabel(incident.status)),
  ];
}

function headerCell(text: string) {
  return new Cell(new Txt(text).bold().fontSize(8).color('#64748b').end).fillColor('#f1f5f9').margin([3, 6, 3, 6]).end;
}

function bodyCell(text: string, options?: { noWrap?: boolean }) {
  let txt = new Txt(text).fontSize(8).color('#334155');
  if (options?.noWrap) {
    txt = txt.noWrap();
  }
  return new Cell(txt.end).margin([3, 5, 3, 5]).end;
}

function formatGeneratedAt(date: Date): string {
  return new Intl.DateTimeFormat('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  }).format(date);
}
